import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { collection, doc, getDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { DB, FUNCTIONS } from 'src/contexts/FirebaseContext';
import { httpsCallable } from 'firebase/functions';

export interface AccountData {
  capabilities: {};
  charges_enabled: boolean;
  country: string;
  default_currency: string;
  details_submitted: boolean;
  id: string;
  object: string;
  payouts_enabled: boolean;
  settings: {};
  type: string;
}

export interface StripeAccountData {
  data: {
    account: AccountData;
  };
}

// Subscription types
export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  stripePriceId: string;
  stripeProductId: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  features: string[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserSubscription {
  subscriptionId: string;
  userId: string;
  customerId: string;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'incomplete';
  priceId: string;
  productId: string;
  planName: string;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  canceledAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Invoice {
  invoiceId: string;
  subscriptionId: string;
  customerId: string;
  amountPaid: number;
  currency: string;
  status: string;
  paidAt: Date;
  periodStart: Date;
  periodEnd: Date;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
  createdAt: Date;
}

export interface CreateCheckoutSessionRequest {
  userId: string;
  priceId: string;
  planName: string;
  successUrl: string;
  cancelUrl: string;
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface CreatePortalSessionRequest {
  customerId: string;
  returnUrl: string;
}

export interface CreatePortalSessionResponse {
  url: string;
}

// API
export const stripeApi = createApi({
  reducerPath: 'stripeApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Subscription', 'SubscriptionPlans', 'Invoices'],
  endpoints: (builder) => ({
    // Get all available subscription plans
    getSubscriptionPlans: builder.query<SubscriptionPlan[], void>({
      async queryFn() {
        try {
          const plansRef = collection(DB, 'subscription_plans');
          const q = query(plansRef, where('isActive', '==', true));
          const querySnapshot = await getDocs(q);

          const plans: SubscriptionPlan[] = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
            } as SubscriptionPlan;
          });

          return { data: plans };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      providesTags: ['SubscriptionPlans'],
    }),

    // Get user's current subscription
    getUserSubscription: builder.query<UserSubscription | null, string>({
      async queryFn(userId) {
        try {
          const subscriptionsRef = collection(DB, 'subscriptions');
          const q = query(subscriptionsRef, where('userId', '==', userId));
          const querySnapshot = await getDocs(q);

          if (querySnapshot.empty) {
            return { data: null };
          }

          // Get the most recent active subscription
          const subs = querySnapshot.docs
            .map((doc) => {
              const data = doc.data();
              return {
                ...data,
                currentPeriodStart: data.currentPeriodStart?.toDate() || new Date(),
                currentPeriodEnd: data.currentPeriodEnd?.toDate() || new Date(),
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
                canceledAt: data.canceledAt?.toDate(),
              } as UserSubscription;
            })
            .filter((sub) => sub.status === 'active' || sub.status === 'past_due');

          const subscription = subs.length > 0 ? subs[0] : null;

          return { data: subscription };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      providesTags: ['Subscription'],
    }),

    // Get user's invoices
    getUserInvoices: builder.query<Invoice[], string>({
      async queryFn(userId) {
        try {
          // First get user's subscription to find customerId
          const subscriptionsRef = collection(DB, 'subscriptions');
          const subQuery = query(subscriptionsRef, where('userId', '==', userId));
          const subSnapshot = await getDocs(subQuery);

          if (subSnapshot.empty) {
            return { data: [] };
          }

          const customerId = subSnapshot.docs[0].data().customerId;

          // Get invoices for this customer
          const invoicesRef = collection(DB, 'invoices');
          const invQuery = query(invoicesRef, where('customerId', '==', customerId));
          const invSnapshot = await getDocs(invQuery);

          const invoices: Invoice[] = invSnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              ...data,
              paidAt: data.paidAt?.toDate() || new Date(),
              periodStart: data.periodStart?.toDate() || new Date(),
              periodEnd: data.periodEnd?.toDate() || new Date(),
              createdAt: data.createdAt?.toDate() || new Date(),
            } as Invoice;
          });

          // Sort by creation date, newest first
          invoices.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

          return { data: invoices };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      providesTags: ['Invoices'],
    }),

    // Create Stripe Checkout session
    createCheckoutSession: builder.mutation<CreateCheckoutSessionResponse, CreateCheckoutSessionRequest>({
      async queryFn(request) {
        try {
          const createCheckoutSession = httpsCallable<CreateCheckoutSessionRequest, CreateCheckoutSessionResponse>(
            FUNCTIONS,
            'createSubscriptionCheckout'
          );

          const result = await createCheckoutSession(request);
          return { data: result.data };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      invalidatesTags: ['Subscription'],
    }),

    // Create Stripe Customer Portal session
    createPortalSession: builder.mutation<CreatePortalSessionResponse, CreatePortalSessionRequest>({
      async queryFn(request) {
        try {
          const createPortalSession = httpsCallable<CreatePortalSessionRequest, CreatePortalSessionResponse>(
            FUNCTIONS,
            'createSubscriptionPortal'
          );

          const result = await createPortalSession(request);
          return { data: result.data };
        } catch (error: any) {
          return { error: error.message };
        }
      },
    }),

    // Cancel subscription
    cancelSubscription: builder.mutation<{ success: boolean }, string>({
      async queryFn(subscriptionId) {
        try {
          const cancelSubscription = httpsCallable<{ subscriptionId: string }, { success: boolean }>(
            FUNCTIONS,
            'cancelSubscription'
          );

          const result = await cancelSubscription({ subscriptionId });
          return { data: result.data };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      invalidatesTags: ['Subscription'],
    }),
  }),
});

export const {
  useGetSubscriptionPlansQuery,
  useGetUserSubscriptionQuery,
  useGetUserInvoicesQuery,
  useCreateCheckoutSessionMutation,
  useCreatePortalSessionMutation,
  useCancelSubscriptionMutation,
} = stripeApi;
